import React, { Component } from 'react';
import {
  Button,
  Modal,
  Form,
  Alert
} from "react-bootstrap";
import moment from 'moment';

class CommitCreator extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 0, // 0: not started, 1: main messages, 2: sub messages, 3: form
      mainMessageIds: new Set(),
      subMessageIds: new Set(),
      showCommitForm: false,
      formData: {
        commit_mid: '',
        version: '',
        friendly_version: '',
        type: 'commit',
        status: 'draft',
        title: '',
        commit_message: ''
      }
    };
  }

  handleStartCommit = () => {
    this.setState({ 
      step: 1,
      mainMessageIds: new Set(),
      subMessageIds: new Set()
    });
    // Notify parent to enter selection mode
    if (this.props.onStartCommit) {
      this.props.onStartCommit();
    }
  }

  handleDoneStep1 = () => {
    this.setState({ 
      step: 2,
      // Keep mainMessageIds, reset subMessageIds
      subMessageIds: new Set()
    });
    // Reset selection in parent
    if (this.props.onResetSelection) {
      this.props.onResetSelection();
    }
  }

  handleDoneStep2 = () => {
    this.setState({ 
      step: 3,
      showCommitForm: true
    });
    // Reset selection in parent
    if (this.props.onResetSelection) {
      this.props.onResetSelection();
    }
  }

  handleMessageSelect = (messageId) => {
    const { step, mainMessageIds, subMessageIds } = this.state;
    
    if (step === 1) {
      const newMainMessageIds = new Set(mainMessageIds);
      if (newMainMessageIds.has(messageId)) {
        newMainMessageIds.delete(messageId);
      } else {
        newMainMessageIds.add(messageId);
      }
      this.setState({ mainMessageIds: newMainMessageIds });
    } else if (step === 2) {
      const newSubMessageIds = new Set(subMessageIds);
      if (newSubMessageIds.has(messageId)) {
        newSubMessageIds.delete(messageId);
      } else {
        newSubMessageIds.add(messageId);
      }
      this.setState({ subMessageIds: newSubMessageIds });
    }

    // Call parent's message select handler
    if (this.props.onMessageSelect) {
      this.props.onMessageSelect(messageId);
    }
  }

  componentDidUpdate(prevProps) {
    // Update mainMessageIds or subMessageIds when selectedMessages changes
    if (prevProps.selectedMessages !== this.props.selectedMessages) {
      const { step } = this.state;
      if (step === 1) {
        this.setState({ mainMessageIds: new Set(this.props.selectedMessages) });
      } else if (step === 2) {
        this.setState({ subMessageIds: new Set(this.props.selectedMessages) });
      }
    }
  }

  handleFormChange = (e) => {
    const { name, value } = e.target;
    this.setState(prevState => ({
      formData: {
        ...prevState.formData,
        [name]: value
      }
    }));
  }

  generateCommitId = () => {
    return 'commit_' + Math.random().toString(36).substr(2, 9);
  }

  handleSubmit = async () => {
    const { formData, mainMessageIds, subMessageIds } = this.state;

    // Generate commit_mid and version if empty
    const payload = {
      ...formData,
      commit_mid: formData.commit_mid || this.generateCommitId(),
      version: formData.version || moment().format(),
      main_message_ids: Array.from(mainMessageIds),
      sub_message_ids: Array.from(subMessageIds),
      main_doc_ids: [],
      sub_doc_ids: [],
      checkpoint_data: {
        tests: "",
        answers: "",
        profile_ai: "",
        profile_user: "",
        content: ""
      },
      metadata: {}
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/k3commit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to create commit');
      }

      // Reset state after successful submission
      this.setState({
        step: 0,
        mainMessageIds: new Set(),
        subMessageIds: new Set(),
        showCommitForm: false,
        formData: {
          commit_mid: '',
          version: '',
          friendly_version: '',
          type: 'commit',
          status: 'draft',
          title: '',
          commit_message: ''
        }
      });

      // Notify parent
      if (this.props.onCommitCreated) {
        this.props.onCommitCreated();
      }

    } catch (error) {
      console.error('Error creating commit:', error);
      // Handle error (show message to user)
    }
  }

  renderStepIndicator = () => {
    const { step } = this.state;
    if (step === 0) return null;

    let message = '';
    switch (step) {
      case 1:
        message = 'Step 1: Please select main messages.';
        break;
      case 2:
        message = 'Step 2: Please select sub messages.';
        break;
      case 3:
        message = 'Step 3: Fill commit information.';
        break;
      default:
        return null;
    }

    return (
      <Alert variant="info" className="mb-3 d-flex justify-content-between align-items-center">
        <span>{message}</span>
        <Button 
          variant="outline-danger" 
          size="sm"
          onClick={() => {
            this.setState({ 
              step: 0,
              mainMessageIds: new Set(),
              subMessageIds: new Set()
            });
            if (this.props.onResetSelection) {
              this.props.onResetSelection();
            }
          }}
        >
          X
        </Button>
      </Alert>
    );
  }

  renderCommitForm = () => {
    const { formData } = this.state;

    return (
      <Modal show={this.state.showCommitForm} onHide={() => this.setState({ showCommitForm: false })}>
        <Modal.Header closeButton>
          <Modal.Title>Create Commit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Commit MID</Form.Label>
              <Form.Control
                type="text"
                name="commit_mid"
                value={formData.commit_mid}
                onChange={this.handleFormChange}
                placeholder="Leave empty for auto-generate"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Version</Form.Label>
              <Form.Control
                type="text"
                name="version"
                value={formData.version}
                onChange={this.handleFormChange}
                placeholder="Leave empty for current timestamp"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Friendly Version</Form.Label>
              <Form.Control
                type="text"
                name="friendly_version"
                value={formData.friendly_version}
                onChange={this.handleFormChange}
                placeholder="e.g., v1.0.0"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Type</Form.Label>
              <Form.Select
                name="type"
                value={formData.type}
                onChange={this.handleFormChange}
              >
                <option value="commit">Commit</option>
                <option value="checkpoint">Checkpoint</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={this.handleFormChange}
              >
                <option value="draft">Draft</option>
                <option value="final">Final</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={this.handleFormChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Commit Message</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="commit_message"
                value={formData.commit_message}
                onChange={this.handleFormChange}
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.setState({ showCommitForm: false })}>
            Cancel
          </Button>
          <Button variant="primary" onClick={this.handleSubmit}>
            Create Commit
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    const { step, mainMessageIds, subMessageIds } = this.state;

    return (
      <div className="commit-creator">
        {this.renderStepIndicator()}

        {step === 0 && (
          <Button 
            variant="danger"
            onClick={this.handleStartCommit}
            className="me-2 btn-sm"
          >
            Commit
          </Button>
        )}

        {step === 1 && mainMessageIds.size > 0 && (
          <Button 
            variant="success"
            onClick={this.handleDoneStep1}
            className="me-2"
          >
            Done Step 1 ({mainMessageIds.size} selected)
          </Button>
        )}

        {step === 2 && (
          <Button 
            variant="success"
            onClick={this.handleDoneStep2}
            className="me-2"
          >
            Done Step 2 ({subMessageIds.size} selected)
          </Button>
        )}

        {this.renderCommitForm()}
      </div>
    );
  }
}

export default CommitCreator; 